import type { DynamicUIDetailSpecific } from "../models/DynamicUIDetailSpecific.interface";
import LxcError from "~/core/models/LxcError.class";
import { useGetGenericUiConfig } from "~/modules/dtwin/composables/useGetGenericUiConfig";
import type { DynamicUI } from "~/modules/dtwin/models/DynamicUI.interface";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import type { DynamicUIGroup } from "~/modules/dtwin/models/DynamicUIGrouped.interface";
import dtwinsService from "~/modules/dtwin/services/dtwin.service";
import { getMergedOrGenericUIConfig } from "~/modules/dtwin/utils/getMergedOrGenericUIConfig";
import { groupBy } from "~/modules/dtwin/utils/mergeUIConfig";

const { getGenericUiConfig } = useGetGenericUiConfig();

export function useFetchDtwinModelUIConfig({
  organizationCode,
}: {
  organizationCode: number | undefined;
}) {
  const result: Ref<DynamicUIGroup | undefined> = ref();
  const error: Ref<LxcError | undefined> = ref();
  const isLoading: Ref<boolean> = ref(false);

  async function fetchData(modelUIConfigId?: string) {
    isLoading.value = true;

    if (!organizationCode) {
      throw new Error("Organization code is undefined");
    }

    let uiConfigProperties: DynamicUI | undefined;

    if (modelUIConfigId) {
      const response =
        await dtwinsService.getDeviceModelUiConfigByDeviceModelUid(
          organizationCode,
          modelUIConfigId,
        );
      if (LxcError.check(response)) {
        uiConfigProperties = getGenericUiConfig();
      } else {
        uiConfigProperties = getMergedOrGenericUIConfig(response?.properties);
      }
    } else {
      uiConfigProperties = getGenericUiConfig();
    }

    const resultProperties: DynamicUIGroup = {
      svgIcon: uiConfigProperties?.svgIcon,
      list: {
        columns: uiConfigProperties?.list.columns || [],
      },
      details: {
        description: {},
        dataviz: uiConfigProperties?.details.dataviz,
        logs: uiConfigProperties?.details.logs,
      },
    };

    groupByBlockAndColumns("description", resultProperties, uiConfigProperties);
    groupByBlockAndColumns(
      "additionalMetadata",
      resultProperties,
      uiConfigProperties,
    );
    groupByBlockAndColumns("certificate", resultProperties, uiConfigProperties);
    groupByBlockAndColumns("firmware", resultProperties, uiConfigProperties);
    groupByBlockAndColumns(
      "configuration",
      resultProperties,
      uiConfigProperties,
    );

    result.value = resultProperties as DynamicUIGroup;

    isLoading.value = false;
  }

  /**
   * Group the details by block and columns
   * This made it easier to display the details in the UI
   * @param key
   * @param resultProperties
   * @param uiConfigProperties
   */
  function groupByBlockAndColumns(
    key:
      | "description"
      | "additionalMetadata"
      | "configuration"
      | "firmware"
      | "certificate",
    resultProperties: DynamicUIGroup,
    uiConfigProperties?: DynamicUI,
  ) {
    if (uiConfigProperties?.details && uiConfigProperties?.details[key]) {
      if (Array.isArray(uiConfigProperties?.details[key])) {
        if (uiConfigProperties?.details[key].length > 0) {
          const groupedByBlock = groupBy(
            uiConfigProperties?.details[key],
            "blockId",
          );
          const groupedByColumn: Record<
            string,
            Record<string, DynamicUIDetail[]>
          > = {};
          for (const [key, value] of Object.entries(groupedByBlock)) {
            groupedByColumn[key] = groupBy(value, "column");
          }
          resultProperties.details[key] = groupedByColumn;
        }
      } else {
        resultProperties.details[key] = uiConfigProperties?.details[
          key
        ] as DynamicUIDetailSpecific;
      }
    }
  }

  return {
    result,
    isLoading,
    error,
    fetchData,
  };
}
